import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { JOBCARD_STATUSES, STATUS_LABELS } from '@garage/shared'
import { api } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useFormatters, useDebounced, useSettings } from '@/lib/hooks.js'
import {
  Button,
  Segmented,
  DataTable,
  PageHeader,
  EmptyState,
  TextField,
  type Column,
} from '@/ui'
import { SearchIcon } from '@/ui/icons.js'
import { StatusChip } from '@/components/StatusChip.js'
import { useState, useEffect } from 'react'

/**
 * A filterable, searchable, paginated list of jobcards. The status filter
 * (open, draft, awaiting approval, etc.) is driven by URL query params so it
 * survives a refresh and back button. Search is debounced.
 */
export function JobcardsPage() {
  const navigate = useNavigate()
  const formatters = useFormatters()
  const { t } = useTranslation()
  const { data: settings } = useSettings()
  const [searchParams, setSearchParams] = useSearchParams()

  // Read filter and search from URL, defaulting to "open" status
  const statusFilter = (searchParams.get('status') ?? 'open') as string
  const searchQuery = searchParams.get('q') ?? ''
  const pageParam = searchParams.get('page')
  const currentPage = pageParam ? parseInt(pageParam, 10) : 1

  // Local search state for controlled input
  const [localSearch, setLocalSearch] = useState(searchQuery)
  const debouncedSearch = useDebounced(localSearch, 250)

  // Update URL when debounced search changes
  useEffect(() => {
    if (debouncedSearch !== searchQuery) {
      const newParams = new URLSearchParams(searchParams)
      if (debouncedSearch) {
        newParams.set('q', debouncedSearch)
      } else {
        newParams.delete('q')
      }
      newParams.set('page', '1')
      setSearchParams(newParams, { replace: true })
    }
  }, [debouncedSearch, searchQuery, searchParams, setSearchParams])

  // Fetch jobcards
  const { data: result, isLoading, error, refetch } = useQuery({
    queryKey: qk.jobcards({ status: statusFilter, q: debouncedSearch, page: currentPage }),
    queryFn: () =>
      api.jobcards.list({
        status: statusFilter as any,
        q: debouncedSearch || undefined,
        page: currentPage,
      }),
  })

  // Handle status filter change
  const handleStatusChange = (status: string) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('status', status)
    newParams.set('page', '1')
    setSearchParams(newParams)
  }

  // Handle pagination
  const handleNextPage = () => {
    if (!result || currentPage * result.pageSize >= result.total) return
    const newParams = new URLSearchParams(searchParams)
    newParams.set('page', String(currentPage + 1))
    setSearchParams(newParams)
  }

  const handlePrevPage = () => {
    if (currentPage <= 1) return
    const newParams = new URLSearchParams(searchParams)
    newParams.set('page', String(currentPage - 1))
    setSearchParams(newParams)
  }

  // Determine empty state scenarios
  const isFilteredEmpty =
    !isLoading &&
    result &&
    result.items.length === 0 &&
    (statusFilter !== 'all' || debouncedSearch)

  const isCompletelyEmpty =
    !isLoading && result && result.total === 0 && !debouncedSearch && statusFilter === 'all'

  // Build segmented control options: open, all concrete statuses, then all
  const segmentedOptions = [
    { value: 'open', label: t('status.open') },
    ...JOBCARD_STATUSES.map((status) => ({
      value: status,
      label: t(`status.${status}`, STATUS_LABELS[status]),
    })),
    { value: 'all', label: t('status.all') },
  ]

  /*
   * Every width is a percentage and the table is locked to `fixedLayout`, so
   * the eight columns always sum to the panel's own width — a long client
   * name or VIN wraps onto a second line instead of stretching the table
   * past its container into a horizontal scrollbar. Number, Plate, Status,
   * Cost, Total and Updated are all naturally short and stay single-line;
   * Client and Car are the two that can run long, so those wrap.
   */
  const rows = result?.items ?? []
  const columns: Column<(typeof rows)[number]>[] = [
    {
      key: 'number',
      header: t('jobcards.columns.number'),
      cell: (row) => <div className="ref">{row.number}</div>,
      width: 'w-[12%]',
    },
    {
      key: 'clientName',
      header: t('jobcards.columns.client'),
      cell: (row) => (
        <div className={row.clientName ? 'text-on-surface' : 'text-on-surface-variant'}>
          {row.clientName || t('common.noCustomerYet')}
        </div>
      ),
      width: 'w-[15%]',
      wrap: true,
    },
    {
      key: 'carLabel',
      header: t('jobcards.columns.car'),
      cell: (row) => <div>{row.carLabel}</div>,
      width: 'w-[19%]',
      wrap: true,
    },
    {
      key: 'plate',
      header: t('jobcards.columns.plate'),
      /*
       * A car that arrived on a scanner has a VIN and no plate yet, and a row
       * identifying it by neither is a row nobody can match to the car in the
       * yard. The VIN stands in, dimmed, so it is clearly not a plate.
       */
      cell: (row) =>
        row.plate ? (
          <div className="ref">{row.plate}</div>
        ) : row.vin ? (
          <div className="ref text-on-surface-variant" title={`VIN ${row.vin}`}>
            {row.vin}
          </div>
        ) : null,
      width: 'w-[13%]',
      wrap: true,
      hideOnMobile: true,
    },
    {
      key: 'status',
      header: t('jobcards.columns.status'),
      cell: (row) => (
          <div className="flex flex-col items-start gap-1">
            <StatusChip status={row.status} />
            {row.sentOut?.map((item) => (
              <span
                key={item.entryId}
                className="text-label-sm text-warning"
                title={`${item.itemSent} · ${formatters.dateTime(item.since)}`}
              >
                {t(`timeline.serviceType.${item.serviceType}`)} · {item.vendorName}
              </span>
            ))}
          </div>
        ),
      width: 'w-[11%]',
    },
    {
      key: 'costMinor',
      header: t('jobcards.columns.cost', { currency: settings?.currencySymbol ?? 'AED' }),
      cell: (row) => (
        <div className="tabular text-on-surface-variant">{formatters.amount(row.costMinor)}</div>
      ),
      numeric: true,
      width: 'w-[8%]',
      hideOnMobile: true,
    },
    {
      key: 'totalMinor',
      header: t('jobcards.columns.total', { currency: settings?.currencySymbol ?? 'AED' }),
      cell: (row) => <div className="tabular">{formatters.amount(row.totalMinor)}</div>,
      numeric: true,
      width: 'w-[9%]',
    },
    {
      key: 'updatedAt',
      header: t('jobcards.columns.updated'),
      cell: (row) => <div>{formatters.date(row.updatedAt)}</div>,
      hideOnMobile: true,
      width: 'w-[10%]',
    },
  ]

  return (
    <div className="space-y-8">
      <PageHeader
        title={t('jobcards.title')}
        actions={
          <Button
            variant="filled"
            onClick={() => navigate('/jobcards/new')}
          >
            {t('common.newJobcard')}
          </Button>
        }
      />

      {/* Error state */}
      {error && (
        <EmptyState
          title={t('jobcards.failedToLoadJobcards')}
          description={t('common.pleaseTryAgain')}
          action={
            <Button variant="outlined" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          }
        />
      )}

      {!error && (
        <div className="space-y-6">
          {/* Control row: Segmented filter on left, search on right */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center sm:justify-between">
            <Segmented
              options={segmentedOptions}
              value={statusFilter}
              onChange={handleStatusChange}
              label={t('dashboard.jobcardStatus')}
              scrollable
              className="no-scrollbar"
            />
            <div className="w-full sm:max-w-xs">
              <TextField
                label={t('common.search')}
                placeholder={t('jobcards.searchPlaceholder')}
                value={localSearch}
                onChange={setLocalSearch}
                leading={<SearchIcon className="w-4 h-4" />}
              />
            </div>
          </div>

          {/* Empty states */}
          {!isLoading && isCompletelyEmpty && (
            <EmptyState
              title={t('jobcards.noJobcardsYet')}
              description={t('jobcards.createFirstJobcard')}
              action={
                <Button
                  variant="outlined"
                  onClick={() => navigate('/jobcards/new')}
                >
                  {t('common.newJobcard')}
                </Button>
              }
            />
          )}

          {!isLoading && isFilteredEmpty && (
            <EmptyState
              title={t('jobcards.noMatches')}
              description={t('jobcards.adjustFilters')}
              action={
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSearchParams(new URLSearchParams({ status: 'open', page: '1' }))
                    setLocalSearch('')
                  }}
                >
                  {t('jobcards.clearFilters')}
                </Button>
              }
            />
          )}

          {/* Table and pagination */}
          {(isLoading || (result && result.items.length > 0)) && (
            <div>
              <DataTable
                columns={columns}
                rows={rows}
                rowKey={(row) => row.id}
                onRowClick={(row) => navigate(`/jobcards/${row.id}`)}
                loading={isLoading}
                stackOnMobile
                fixedLayout
              />

              {/* Pagination */}
              {result && result.items.length > 0 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-outline-variant">
                  <div className="text-label-md text-on-surface-variant">
                    {t('common.showingRange', {
                      from: (currentPage - 1) * result.pageSize + 1,
                      to: Math.min(currentPage * result.pageSize, result.total),
                      total: result.total,
                    })}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outlined"
                      onClick={handlePrevPage}
                      disabled={currentPage <= 1}
                    >
                      {t('common.previous')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleNextPage}
                      disabled={currentPage * result.pageSize >= result.total}
                    >
                      {t('common.next')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
